import { Activity, Cpu, HardDrive, MemoryStick } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatBytes, formatPercent, formatUptime, sparklinePoints } from "./metrics-format";
import { useServerMetrics } from "./use-server-metrics";

const SPARK_W = 72;
const SPARK_H = 18;

/**
 * Server-health widget for the instance administrator (#284): the production host's CPU, RAM and
 * disk in a corner of the panel, so load is visible without an SSH session. Mounted in AppLayout
 * and gated on `me.platformAdmin`, so it never renders for ordinary staff. No GPU gauge: the
 * Hetzner VPS has no GPU (ADR-0020). Starts collapsed to a small pill to stay out of the way.
 */
export function ServerHealthWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { latest, cpuHistory, memHistory, diskHistory, error } = useServerMetrics(true);

  const cpuPct = latest?.cpu ?? null;
  const memPct = latest ? latest.memory.used / latest.memory.total : null;
  const diskPct = latest ? latest.disk.used / latest.disk.total : null;

  return (
    <div className="absolute bottom-3 left-3 z-20 select-none">
      {open ? (
        <div className="w-56 rounded border border-zinc-700 bg-zinc-900/95 p-3 text-xs text-zinc-200 shadow-xl">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mb-2 flex w-full items-center gap-1.5 font-semibold text-zinc-100"
            title={t("serverHealth.hint")}
          >
            <Activity size={13} className="text-emerald-400" />
            {t("serverHealth.title")}
          </button>
          {error || !latest ? (
            <p className="text-zinc-500">{t("serverHealth.unavailable")}</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              <Gauge
                icon={<Cpu size={12} />}
                label={t("serverHealth.cpu")}
                value={formatPercent(cpuPct)}
                fraction={cpuPct}
                history={cpuHistory}
              />
              <Gauge
                icon={<MemoryStick size={12} />}
                label={t("serverHealth.memory")}
                value={`${formatPercent(memPct)} · ${formatBytes(latest.memory.used)} / ${formatBytes(latest.memory.total)}`}
                fraction={memPct}
                history={memHistory}
              />
              <Gauge
                icon={<HardDrive size={12} />}
                label={t("serverHealth.disk")}
                value={`${formatPercent(diskPct)} · ${formatBytes(latest.disk.used)} / ${formatBytes(latest.disk.total)}`}
                fraction={diskPct}
                history={diskHistory}
              />
              <p className="text-[11px] text-zinc-500">
                {t("serverHealth.load", {
                  load: latest.load1.toFixed(2),
                  cores: latest.cpuCount,
                })}{" "}
                · {t("serverHealth.uptime", { value: formatUptime(latest.uptime) })}
              </p>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 rounded border border-zinc-700 bg-zinc-900/90 px-2 py-1 text-xs text-zinc-300 shadow hover:bg-zinc-800"
          title={t("serverHealth.hint")}
        >
          <Activity size={13} className={error ? "text-zinc-500" : "text-emerald-400"} />
          {latest && !error ? (
            <span className="tabular-nums">
              {formatPercent(cpuPct)} · {formatPercent(memPct)}
            </span>
          ) : (
            t("serverHealth.title")
          )}
        </button>
      )}
    </div>
  );
}

function Gauge({
  icon,
  label,
  value,
  fraction,
  history,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  fraction: number | null;
  history: number[];
}) {
  // Green/amber/red by load so a glance tells the state; muted when there is no sample yet.
  const tone =
    fraction === null
      ? "text-zinc-500"
      : fraction >= 0.9
        ? "text-red-400"
        : fraction >= 0.7
          ? "text-amber-400"
          : "text-emerald-400";
  return (
    <div className="flex items-center gap-2">
      <span className={tone}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-1">
          <span className="text-zinc-400">{label}</span>
          <span className="truncate tabular-nums text-zinc-300" title={value}>
            {value}
          </span>
        </div>
        <svg
          width={SPARK_W}
          height={SPARK_H}
          viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
          className="mt-0.5 w-full"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polyline
            points={sparklinePoints(history, SPARK_W, SPARK_H)}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            className={tone}
          />
        </svg>
      </div>
    </div>
  );
}
